import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { Input } from '@/components/ui/input';
import { toApiProblem, toastProblem } from '@/lib/errors';

import {
  InlineControlRow,
  SettingsToggleRow,
  inlineNumberInputClass,
} from '../components/SettingsSection';

// Keep in sync with config('carrier.limits.*') on the backend.
const MIN_DELAY_MINUTES = 1;
const MAX_DELAY_MINUTES = 1440;
const MIN_RADIUS_KM = 1;
const MAX_RADIUS_KM = 50;

interface PointReminderSectionProps {
  reminderEnabled: boolean;
  reminderDelayMinutes: number;
  autoSelectEnabled: boolean;
  autoSelectRadiusKm: number;
}

/**
 * Post-checkout follow-up for point-based orders left without a pickup point.
 * Auto-select (assign the nearest point) and the reminder email are two
 * independent toggles sharing one delay; either alone schedules the follow-up.
 * The shared delay input renders under auto-select ("Within X km · after Y
 * minutes") or, when only the email is on, under the reminder row. Saves
 * immediately - toggles on click, number fields on blur, each through its own
 * mutation so none blocks another.
 */
export function PointReminderSection({
  reminderEnabled,
  reminderDelayMinutes,
  autoSelectEnabled,
  autoSelectRadiusKm,
}: PointReminderSectionProps) {
  const { t } = useTranslation('settings');

  const updateReminder = useUpdateMerchantSettings();
  const updateDelay = useUpdateMerchantSettings();
  const updateAutoSelect = useUpdateMerchantSettings();
  const updateRadius = useUpdateMerchantSettings();

  const [pendingReminder, setPendingReminder] = useState<boolean | null>(null);
  const enabled = pendingReminder ?? reminderEnabled;

  const [pendingAutoSelect, setPendingAutoSelect] = useState<boolean | null>(null);
  const autoSelect = pendingAutoSelect ?? autoSelectEnabled;

  const [delayInput, setDelayInput] = useState(String(reminderDelayMinutes));
  const [radiusInput, setRadiusInput] = useState(String(autoSelectRadiusKm));

  useEffect(() => {
    if (pendingReminder !== null && pendingReminder === reminderEnabled) {
      setPendingReminder(null);
    }
  }, [pendingReminder, reminderEnabled]);

  useEffect(() => {
    if (pendingAutoSelect !== null && pendingAutoSelect === autoSelectEnabled) {
      setPendingAutoSelect(null);
    }
  }, [pendingAutoSelect, autoSelectEnabled]);

  useEffect(() => {
    setDelayInput(String(reminderDelayMinutes));
  }, [reminderDelayMinutes]);

  useEffect(() => {
    setRadiusInput(String(autoSelectRadiusKm));
  }, [autoSelectRadiusKm]);

  const handleReminderToggle = (checked: boolean) => {
    if (updateReminder.isPending) return;
    setPendingReminder(checked);
    updateReminder.mutate(
      { settings: { point_reminder_enabled: checked } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPendingReminder(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  const handleAutoSelectToggle = (checked: boolean) => {
    if (updateAutoSelect.isPending) return;
    setPendingAutoSelect(checked);
    updateAutoSelect.mutate(
      { settings: { point_auto_select_enabled: checked } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPendingAutoSelect(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  const saveNumber = (
    raw: string,
    saved: number,
    min: number,
    max: number,
    setInput: (value: string) => void,
    mutate: (value: number) => void,
  ) => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < min) {
      setInput(String(saved));
      return;
    }
    const clamped = Math.min(parsed, max);
    setInput(String(clamped));
    if (clamped === saved) return;
    mutate(clamped);
  };

  const saveDelay = () =>
    saveNumber(
      delayInput,
      reminderDelayMinutes,
      MIN_DELAY_MINUTES,
      MAX_DELAY_MINUTES,
      setDelayInput,
      (value) =>
        updateDelay.mutate(
          { settings: { point_reminder_delay_minutes: value } },
          {
            onSuccess: () => toast.success(t('saved')),
            onError: (error) => {
              setDelayInput(String(reminderDelayMinutes));
              toastProblem(toApiProblem(error));
            },
          },
        ),
    );

  const saveRadius = () =>
    saveNumber(
      radiusInput,
      autoSelectRadiusKm,
      MIN_RADIUS_KM,
      MAX_RADIUS_KM,
      setRadiusInput,
      (value) =>
        updateRadius.mutate(
          { settings: { point_auto_select_radius_km: value } },
          {
            onSuccess: () => toast.success(t('saved')),
            onError: (error) => {
              setRadiusInput(String(autoSelectRadiusKm));
              toastProblem(toApiProblem(error));
            },
          },
        ),
    );

  const delayField = (
    <Input
      type="number"
      inputMode="numeric"
      min={MIN_DELAY_MINUTES}
      max={MAX_DELAY_MINUTES}
      value={delayInput}
      onChange={(event) => setDelayInput(event.target.value)}
      onBlur={saveDelay}
      aria-label={t('checkout.pointReminder.delayLabel')}
      className={inlineNumberInputClass}
    />
  );

  return (
    <>
      {/* Auto-select - independent of the email reminder. */}
      <SettingsToggleRow
        label={t('checkout.pointReminder.autoSelect.label')}
        description={t('checkout.pointReminder.autoSelect.description')}
        checked={autoSelect}
        onCheckedChange={handleAutoSelectToggle}
      >
        {autoSelect ? (
          <InlineControlRow>
            <span>{t('checkout.pointReminder.autoSelect.inlineWithin')}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={MIN_RADIUS_KM}
              max={MAX_RADIUS_KM}
              value={radiusInput}
              onChange={(event) => setRadiusInput(event.target.value)}
              onBlur={saveRadius}
              aria-label={t('checkout.pointReminder.autoSelect.radiusLabel')}
              className={inlineNumberInputClass}
            />
            <span>{t('checkout.pointReminder.autoSelect.inlineAfter')}</span>
            {delayField}
            <span>{t('checkout.pointReminder.inlineMinutes')}</span>
          </InlineControlRow>
        ) : null}
      </SettingsToggleRow>

      {/* Reminder email - independent of auto-select. */}
      <SettingsToggleRow
        label={t('checkout.pointReminder.label')}
        description={t('checkout.pointReminder.reminderDescription')}
        checked={enabled}
        onCheckedChange={handleReminderToggle}
      >
        {enabled && !autoSelect ? (
          <InlineControlRow>
            <span>{t('checkout.pointReminder.inlineSentAfter')}</span>
            {delayField}
            <span>{t('checkout.pointReminder.inlineMinutes')}</span>
          </InlineControlRow>
        ) : null}
      </SettingsToggleRow>
    </>
  );
}
